<template>
<main class="container-1">

    <div class="container-header">
        <Breadcrumb
        v-bind:links="routesBread"
        >
        </Breadcrumb>
        <TabNav :links="routesNav" />
    </div>

    <div class="container-body">
        <div class="password-card">

            <p v-if="myself && myself.username" class="account-line">Cuenta: {{myself.username}}</p>

            <!-- Verificación por correo (2FA) -->
            <div class="code-verification">
              <p class="code-hint">Para cambiar la contraseña, primero verifica tu identidad.</p>
              <div class="code-row">
                <OTPInput v-model="verificationCode" />
                <button type="button" class="btn btn-secondary" :disabled="sendingCode" @click="sendCode">
                  {{ codeSent ? 'Reenviar código' : 'Enviar código a mi correo' }}
                </button>
              </div>
              <p v-if="codeSent" class="code-sent-msg">Código enviado. Revisa tu correo.</p>
            </div>

            <div class="password-field">
                <label for="old_password">Contraseña actual</label>
                <div class="password-input-wrap">
                    <input id="old_password" v-model="account.old_password" :type="showOldPassword ? 'text' : 'password'" autocomplete="current-password"
                    name="contraseña actual" :class="{'error' : errors.has('contraseña actual') }" v-validate="'required'"
                    :placeholder="[[errors.has('contraseña actual') ? errors.first('contraseña actual') : 'Ingresa la contraseña actual']]"/>
                    <button type="button" class="pw-toggle"
                    :aria-label="showOldPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                    @click="showOldPassword = !showOldPassword">
                        <font-awesome-icon :icon="showOldPassword ? 'eye-slash' : 'eye'"></font-awesome-icon>
                    </button>
                </div>
                <p class="field-hint">No puede ser igual a la contraseña actual.</p>
            </div>

            <div class="password-field">
                <label for="new_password">Contraseña nueva</label>
                <div class="password-input-wrap">
                    <input id="new_password" v-model="account.new_password" :type="showNewPassword ? 'text' : 'password'" ref="contraseña nueva" autocomplete="new-password"
                    name="contraseña nueva" :class="{'error' : errors.has('contraseña nueva') }" v-validate="'required|strong_password'"
                    :placeholder="[[errors.has('contraseña nueva') ? errors.first('contraseña nueva') : 'Ingresa la contraseña nueva']]"/>
                    <button type="button" class="pw-toggle"
                    :aria-label="showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                    @click="showNewPassword = !showNewPassword">
                        <font-awesome-icon :icon="showNewPassword ? 'eye-slash' : 'eye'"></font-awesome-icon>
                    </button>
                </div>
                <PasswordStrengthMeter :password="account.new_password || ''"></PasswordStrengthMeter>
                <p class="error-label" v-if="errors.has('contraseña nueva')">{{errors.first('contraseña nueva')}}</p>

                <div class="requirements-panel">
                    <p class="requirements-panel__title">La contraseña debe tener:</p>
                    <PasswordRequirements :password="account.new_password || ''"></PasswordRequirements>
                </div>
            </div>

            <div class="password-field">
                <label for="repeat_password">Repita la contraseña nueva</label>
                <div class="password-input-wrap">
                    <input id="repeat_password" v-model="repeat_password" :type="showRepeatPassword ? 'text' : 'password'" autocomplete="new-password"
                    name="contraseña repetida" :class="{'error' : errors.has('contraseña repetida') }" v-validate="'required|confirmed:contraseña nueva'"  data-vv-as="contraseña nueva"
                    :placeholder="[[errors.has('contraseña repetida') ? errors.first('contraseña repetida') : 'Ingresa la contraseña repetida']]"/>
                    <button type="button" class="pw-toggle"
                    :aria-label="showRepeatPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                    @click="showRepeatPassword = !showRepeatPassword">
                        <font-awesome-icon :icon="showRepeatPassword ? 'eye-slash' : 'eye'"></font-awesome-icon>
                    </button>
                </div>
                <p class="error-label" v-if="errors.has('contraseña repetida')">{{errors.first('contraseña repetida')}}</p>
            </div>

            <p class="error-label" v-if="message">{{message}}</p>

            <div class="card-actions">
                <button class="btn btn-secondary" v-on:click="onClickGoBack()">Volver</button>
                <button class="btn btn-primary" v-on:click="onClickGoNext()">Cambiar contraseña</button>
            </div>

        </div>
    </div>

</main>
</template>

<style lang="scss" scoped>
.password-card {
    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: 8px;
    padding: 1.5rem 1.75rem;
    max-width: 560px;
    margin: 1rem auto 0;
}

.account-line {
    margin: 0 0 1rem;
    font-size: 0.85rem;
    color: var(--color-ghost);
}

.password-field {
    margin-bottom: 1rem;

    label {
        display: block;
        margin-bottom: 0.35rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--color-graphite);
    }
}

.password-input-wrap {
    position: relative;

    input {
        width: 100%;
        box-sizing: border-box;
        padding-right: 2.5rem;

        // Edge dibuja su propio ojo nativo y quedarian dos superpuestos
        &::-ms-reveal,
        &::-ms-clear {
            display: none;
        }
    }
}

.pw-toggle {
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    padding: 0.25rem;
    background: transparent;
    border: none;
    color: var(--color-ghost);
    cursor: pointer;

    &:hover {
        color: var(--color-navy);
    }
}

.field-hint {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: var(--color-ghost);
}

.requirements-panel {
    margin-top: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--color-mist);
    border-radius: 6px;

    &__title {
        margin: 0 0 0.25rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--color-graphite);
    }
}

.card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.25rem;
}

// El .error-label global es position:absolute y se encima con el campo siguiente;
// acá se hace fluir en el layout (Login aún no declara position y arrastra el bug)
.error-label {
    position: static;
    margin: 6px 0 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    color: var(--color-danger);
}

.code-verification {
    background: var(--color-mist);
    border: 1px solid var(--color-line);
    border-radius: 6px;
    padding: 0.85rem 1rem;
    margin-bottom: 1.25rem;
}

.code-hint {
    margin: 0 0 0.65rem;
    font-size: 0.83rem;
    color: var(--color-ghost);
}

.code-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
}



.code-sent-msg {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: var(--color-steel);
}

@media only screen and (max-width: 600px) {
    .password-card {
        max-width: 100%;
        padding: 1rem 1.25rem;
    }
}
</style>

<script>
// @ is an alias to /src
import Breadcrumb from '@/components/Breadcrumb.vue'
import TabNav from '@/components/TabNav.vue'
import PasswordRequirements from '@/components/PasswordRequirements.vue'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter.vue'
import OTPInput from '@/components/OTPInput.vue'

var moment = require('moment');

export default{
    name: 'test',
    data(){
        return{
            myself: null,
            account: {
                id: null,
                new_password: null,
                old_password: null
            },
            repeat_password: null,
            message: null,
            showOldPassword: false,
            showNewPassword: false,
            showRepeatPassword: false,
            verificationCode: '',
            codeSent: false,
            sendingCode: false
        }
    },
    components:{
        Breadcrumb,
        TabNav,
        PasswordRequirements,
        PasswordStrengthMeter,
        OTPInput
    },
    computed: {
        routesBread: function(){
          return [
              {name:'Cuenta', path: '/cuenta/mi-cuenta' },
              {name:'Cambiar contraseña'}
              ]
        },
        routesNav: function(){
          return [
                {name:'Cuenta', path: '/cuenta/mi-cuenta'},
                {name:'Cambiar contraseña' , path: '/cuenta/cambiar-contrasena'},
              ]
        },
    },
    props: [],
    methods:{
        dateFormatter: function (date) {
            moment.locale('es');
            return 'Creado el ' + moment(date).format('LL');
        },
        onClickGoBack: function() {
            window.history.length > 1
                ? this.$router.go(-1)
                : this.$router.push('/cuenta/mi-cuenta')
        },
        onClickGoNext: function() {
            var that = this;
            this.$validator.validateAll()
            .then(function (response) {
                if(response) {
                    console.log('OK')
                    that.changePassword();
                } else {
                    console.log('NOPE')
                }
            })
        },
        sendCode: function() {
            var that = this;
            that.sendingCode = true;
            this.$http.post(this.$store.getters.backendurl + 'api/v1/accounts/password/send-code')
            .then(function() {
                that.codeSent = true;
                that.$store.dispatch('toast/show', { message: 'Código enviado a tu correo.', type: 'info' });
            })
            .catch(function() {
                that.$store.dispatch('toast/show', { message: 'No fue posible enviar el código. Intenta nuevamente.', type: 'error' });
            })
            .finally(function() {
                that.sendingCode = false;
            });
        },
        getMyself: function(){
            var that = this;
            this.$http.get(this.$store.getters.backendurl + 'api/v1/accounts/me')
            .then(function (response) {
                that.myself = response.data;
                that.account.id = response.data.id;
            })
            .catch(function (error) {
                //No bloquear el formulario — backend identifica al usuario por sesión
                //(visor-only no tiene acceso a /accounts/me pero sí puede cambiar su contraseña)
                console.log(error)
            });
        },
        changePassword: function(){
            var that = this;
            that.message = null;
            this.$http.put(this.$store.getters.backendurl + 'api/v1/accounts/password',
            Object.assign({}, this.account, { code: this.verificationCode })
            )
            .then(function () {
                //Confirmación visible (#193) antes de redirigir
                that.$store.dispatch('toast/show', { message: 'Contraseña actualizada', type: 'info' });
                that.$router.push('/cuenta/mi-cuenta')
            })
            .catch(function (error) {
                console.log(error)
                that.message = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Error al cambiar la contraseña.';
            });
        },
    },
    updated () {

    },
    created () {
        //Verificar permisos
        this.$store.getters.getPermissions? true:this.$router.push('/inicio')
        this.$store.getters.getPermissions.visor? true:this.$router.push('/pagina-no-encontrada')
    },
    mounted () {
        this.getMyself();
    }
}
</script>
